import React, { useState, useEffect } from "react";
import { Header, Icon, Segment, Label, Divider } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { capitalizeFirstLetter, toTitleCase } from "../../constants";
import { selectCurrentUser } from "../auth/authSlice";
import { useUpdateCustomerPersonaMutation } from "./customerPersonaApiSlice";
import { Pill, Select as UiSelect, fieldStyle, labelStyle, ui } from "../../components/adminUi";

const GENDER_OPTIONS = [
  { key: "MALE", value: "MALE", text: "남성" },
  { key: "FEMALE", value: "FEMALE", text: "여성" },
  { key: "OTHER", value: "OTHER", text: "기타" },
];

const SUBKEY_LABELS = { value: "내용", expectations: "기대사항" };

const isScalar = v => v === null || ["string", "number", "boolean"].includes(typeof v);

const CustomerPersonaDetail = ({ customer_persona }) => {
  const isSuperuser = useSelector(selectCurrentUser)?.is_superuser === true;
  const [updateCustomerPersona, { isLoading: isSaving }] =
    useUpdateCustomerPersonaMutation();

  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [error, setError] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Reset edit state whenever a different persona is selected.
  useEffect(() => {
    setEditing(false);
    setError("");
    setSaveSuccess(false);
  }, [customer_persona?.id]);

  const startEdit = () => {
    setForm({
      name: customer_persona.name || "",
      gender: customer_persona.gender || "OTHER",
      purpose_of_visit: customer_persona.purpose_of_visit || "",
      // Deep clone so edits don't mutate the prop; only scalar leaves are
      // exposed as fields, so any unexpected nested data is preserved as-is.
      persona: JSON.parse(JSON.stringify(customer_persona.persona || {})),
    });
    setError("");
    setSaveSuccess(false);
    setEditing(true);
  };

  // Update one leaf (persona[attr][subkey]) immutably.
  const setLeaf = (attr, subkey, value) =>
    setForm(f => ({
      ...f,
      persona: { ...f.persona, [attr]: { ...f.persona[attr], [subkey]: value } },
    }));

  const handleSave = async () => {
    // Preserve the original numeric type of leaves the LLM emitted as numbers
    // (e.g. age) — text inputs would otherwise turn them into strings.
    const orig = customer_persona.persona || {};
    const persona = {};
    for (const [attr, attrs] of Object.entries(form.persona)) {
      if (attrs && typeof attrs === "object" && !Array.isArray(attrs)) {
        persona[attr] = {};
        for (const [k, v] of Object.entries(attrs)) {
          const o = orig[attr]?.[k];
          persona[attr][k] =
            typeof o === "number" && typeof v === "string" && v.trim() !== "" && !isNaN(Number(v))
              ? Number(v)
              : v;
        }
      } else {
        persona[attr] = attrs;
      }
    }
    try {
      await updateCustomerPersona({
        id: customer_persona.id,
        name: form.name,
        gender: form.gender,
        purpose_of_visit: form.purpose_of_visit,
        persona,
      }).unwrap();
      setEditing(false);
      setSaveSuccess(true);
    } catch (err) {
      setError("저장에 실패했습니다.");
    }
  };

  if (!customer_persona) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#86868b', padding: '40px' }}>
        <Icon name="hand point left outline" size="huge" style={{ marginBottom: '1rem' }} />
        <p>좌측 목록에서 페르소나를 선택하여 상세 내용을 확인하세요.</p>
      </div>
    );
  }

  if (!customer_persona.is_postprocessed) {
    return (
      <Segment placeholder basic>
        <Header icon>
          <Icon name='spinner' loading />
          페르소나 상세 정보를 생성 중입니다.
          <Header.Subheader>잠시 후 다시 확인해주세요.</Header.Subheader>
        </Header>
      </Segment>
    );
  }

  if (editing) {
    return (
      <div style={{ padding: '10px' }}>
        <Header as="h3" style={{ marginTop: 0 }}>페르소나 수정</Header>
        {error && (
          <div style={{
            background: ui.redBg, color: ui.redFg, borderRadius: '12px',
            padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: 500,
          }}>
            {error}
          </div>
        )}
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>이름</label>
          <input
            style={fieldStyle}
            value={form.name}
            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
          />
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>성별</label>
          <UiSelect
            value={form.gender}
            onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
          >
            {GENDER_OPTIONS.map(o => (
              <option key={o.value} value={o.value}>{o.text}</option>
            ))}
          </UiSelect>
        </div>
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>설명</label>
          <textarea
            style={{ ...fieldStyle, resize: 'vertical', lineHeight: 1.5 }}
            rows={3}
            value={form.purpose_of_visit}
            onChange={e => setForm(f => ({ ...f, purpose_of_visit: e.target.value }))}
          />
        </div>

        <Header as="h4" dividing style={{ marginTop: '24px' }}>페르소나 상세</Header>
        {Object.entries(form.persona).map(([attr, attrs]) => {
          if (!attrs || typeof attrs !== 'object' || Array.isArray(attrs)) return null;
          return (
            <div
              key={attr}
              style={{ backgroundColor: ui.pageBg, padding: '16px 20px', borderRadius: '12px', marginBottom: '12px' }}
            >
              <div style={{ fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: ui.muted, marginBottom: '10px' }}>
                {toTitleCase(attr)}
              </div>
              {Object.entries(attrs).map(([subkey, val]) => {
                if (!isScalar(val)) return null;
                const label = SUBKEY_LABELS[subkey] || toTitleCase(subkey);
                const long = subkey === 'expectations' || String(val).length > 60;
                return (
                  <div key={subkey} style={{ marginBottom: '10px' }}>
                    <label style={{ ...labelStyle, fontSize: '12px', fontWeight: 500, color: '#424245' }}>{label}</label>
                    {long ? (
                      <textarea
                        style={{ ...fieldStyle, background: 'white', resize: 'vertical', lineHeight: 1.5 }}
                        rows={2}
                        value={val}
                        onChange={e => setLeaf(attr, subkey, e.target.value)}
                      />
                    ) : (
                      <input
                        style={{ ...fieldStyle, background: 'white' }}
                        value={val}
                        onChange={e => setLeaf(attr, subkey, e.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
          <Pill variant="ghost" onClick={() => setEditing(false)} disabled={isSaving}>
            취소
          </Pill>
          <Pill variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? '저장 중…' : '저장'}
          </Pill>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '10px' }}>
      {saveSuccess && (
        <div style={{
          background: ui.greenBg, color: ui.greenFg, borderRadius: '12px',
          padding: '10px 14px', marginBottom: '16px', fontSize: '13px', fontWeight: 500,
        }}>
          페르소나가 저장되었습니다.
        </div>
      )}
      {/* The in-store scene — the image the trainee actually sees on the card.
          `situation_url` was already on the serializer (presigned, 15 min); the panel
          simply never rendered it.

          Sized by height, not width. These are generated at 3:4 portrait, so filling
          this ~585px pane would make it 780px tall and push everything else off the
          screen. Capping the height and letting the width follow keeps the whole frame
          visible and uncropped — cropping a scene defeats the point of showing it, since
          what matters is where the customer is standing and what is on the table. */}
      {customer_persona.situation_url && (
        <figure style={{ margin: '0 0 20px', textAlign: 'center' }}>
          <img
            src={customer_persona.situation_url}
            alt={`${customer_persona.int_id ? `${customer_persona.int_id}번 고객` : '고객'} 매장 장면`}
            style={{
              height: 'min(360px, 45vh)',
              width: 'auto',
              maxWidth: '100%',
              aspectRatio: '3 / 4',
              objectFit: 'contain',
              borderRadius: '12px',
              display: 'block',
              margin: '0 auto',
              backgroundColor: '#f2f2f7',
            }}
          />
          <figcaption style={{ fontSize: '11px', color: '#86868b', marginTop: '6px' }}>
            매장 장면 · AI 생성 이미지
          </figcaption>
        </figure>
      )}

      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          {/* N번 고객 leads: it is what the trainee sees, what the client cites, and what
              the Excel sheets are named by. The generated name is a detail, so it sits
              under it rather than standing in for the identifier. */}
          <Header as="h1" style={{ margin: 0, fontSize: '24px' }}>
            {customer_persona.int_id ? `${customer_persona.int_id}번 고객` : '번호 없음'}
            <Header.Subheader style={{ fontSize: '14px', color: '#86868b', marginTop: '2px' }}>
              {customer_persona.name}
            </Header.Subheader>
          </Header>
          <Label
            color={customer_persona.gender === 'MALE' ? 'blue' : customer_persona.gender === 'FEMALE' ? 'pink' : 'grey'} 
            // basic 
            circular
            size="small"
          >
            <Icon name={customer_persona.gender === 'MALE' ? 'man' : customer_persona.gender === 'FEMALE' ? 'woman' : 'user'} style={{ margin: 0 }} />
          </Label>
          {customer_persona.user_info && (
            <Label size="small" color="orange" style={{ borderRadius: '999px' }}>
              <Icon name="user" />
              {customer_persona.user_info.full_name || customer_persona.user_info.username || '사용자'}
            </Label>
          )}
          {isSuperuser && (
            <Pill variant="neutral" onClick={startEdit} style={{ marginLeft: 'auto' }}>
              <Icon name="edit" style={{ margin: 0 }} /> 수정
            </Pill>
          )}
        </div>
        <p style={{ fontSize: '16px', color: '#424245', lineHeight: '1.5' }}>
          {customer_persona.purpose_of_visit}
        </p>
      </div>

      {customer_persona.areas_of_improvement && (
        <div style={{ marginBottom: '24px', backgroundColor: '#f9fafb', padding: '16px 20px', borderRadius: '16px' }}>
          <Header as="h4" style={{ margin: '0 0 8px 0', color: '#1d1d1f', fontSize: '14px' }}>
            맞춤 훈련 영역
          </Header>
          {(() => {
            const aoi = customer_persona.areas_of_improvement || {};
            const comm = aoi.communication || [];
            const products = aoi.product_knowledge || [];
            const hasComm = comm.length > 0;
            const hasProducts = products.length > 0;
            if (!hasComm && !hasProducts) return null;
            return (
              <div style={{ fontSize: '14px', color: '#424245', lineHeight: 1.6 }}>
                {hasComm && (
                  <div style={{ marginBottom: hasProducts ? '6px' : 0 }}>
                    <div style={{ color: '#86868b', marginBottom: '2px' }}>응대 스킬</div>
                    <ul style={{ margin: 0 }}>
                      {comm.map((c, i) => c.text && (
                        <li key={i}>· {c.text}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {hasProducts && (
                  <div>
                    <div style={{ color: '#86868b', marginBottom: '2px' }}>제품</div>
                    <ul style={{ margin: 0 }}>
                      {products.map((p, i) => p.name && (
                        <li key={i}>{p.name}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      <Divider />

      <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        {Object.entries(customer_persona.persona).map(([category, attributes], index) => (
          <div key={index} style={{ backgroundColor: '#f9fafb', padding: '20px', borderRadius: '16px' }}>
            <Header as="h4" style={{ margin: '0 0 16px 0', color: '#1d1d1f', textTransform: 'uppercase', fontSize: '12px', letterSpacing: '0.05em', opacity: 0.6 }}>
              {toTitleCase(category)}
            </Header>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {Object.entries(attributes).map(([key, value], idx) => (
                <div key={idx} style={{ display: 'flex', alignItems: 'flex-start' }}>
                  <div style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    backgroundColor: '#007AFF', 
                    marginTop: '8px',
                    marginRight: '12px', 
                    flexShrink: 0,
                    opacity: 0.6
                  }}></div>
                  <span style={{ fontSize: '15px', color: '#333', lineHeight: '1.5' }}>
                    {capitalizeFirstLetter(`${value}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
export default CustomerPersonaDetail;
